import { formatDistanceToNow, format, parseISO } from 'date-fns';

/**
 * Get time-based greeting
 */
export const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
};

/**
 * Format date as relative time (e.g., "2 hours ago")
 */
export const formatTimeAgo = (dateString) => {
  try {
    const date = typeof dateString === 'string' ? parseISO(dateString) : dateString;
    return formatDistanceToNow(date, { addSuffix: true });
  } catch (error) {
    return 'Recently';
  }
};

/**
 * Format date with custom format string
 */
export const formatDate = (dateString, formatStr = 'MMM d, yyyy') => {
  try {
    const date = typeof dateString === 'string' ? parseISO(dateString) : dateString;
    return format(date, formatStr);
  } catch (error) {
    return dateString;
  }
};

/**
 * Format currency amount
 */
export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

/**
 * Convert category ID to display name
 */
export const getCategoryName = (categoryId) => {
  return categoryId.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};


/**
 * Get initials from full name
 */
export const getInitials = (name) => {
  if (!name) return '?';
  return name
    .split(' ')
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

/**
 * Truncate text to specified length
 */
export const truncateText = (text, maxLength = 100) => {
  if (!text || text.length <= maxLength) return text;
  return text.slice(0, maxLength).trim() + '...';
};

/**
 * Generate random ID
 */
export const generateId = (prefix = 'id') => {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Debounce function for search inputs
 */
export const debounce = (func, wait = 300) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Check if date is today
 */
export const isToday = (dateString) => {
  try {
    const date = typeof dateString === 'string' ? parseISO(dateString) : dateString;
    const today = new Date();
    return date.toDateString() === today.toDateString();
  } catch (error) {
    return false;
  }
};

/**
 * Check if date is in the future
 */
export const isFuture = (dateString) => {
  try {
    const date = typeof dateString === 'string' ? parseISO(dateString) : dateString;
    return date > new Date();
  } catch (error) {
    return false;
  }
};

/**
 * Format phone number
 */
export const formatPhoneNumber = (phone) => {
  const cleaned = ('' + phone).replace(/\D/g, '');
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return '(' + match[1] + ') ' + match[2] + '-' + match[3];
  }
  return phone;
};


/**
 * Validate email format
 */
export const isValidEmail = (email) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
};

/**
 * Validate phone format
 */
export const isValidPhone = (phone) => {
  const cleaned = ('' + phone).replace(/\D/g, '');
  return cleaned.length === 10;
};

/**
 * Get status color classes
 */
export const getStatusColor = (status) => {
  const colors = {
    searching: 'text-blue-600 bg-blue-50',
    quoted: 'text-yellow-600 bg-yellow-50',
    booked: 'text-green-600 bg-green-50',
    confirmed: 'text-green-600 bg-green-50',
    in_progress: 'text-purple-600 bg-purple-50',
    completed: 'text-gray-600 bg-gray-50',
    cancelled: 'text-red-600 bg-red-50',
    pending: 'text-orange-600 bg-orange-50',
  };
  return colors[status] || colors.pending;
};

/**
 * Get urgency color classes
 */
export const getUrgencyColor = (urgency) => {
  const colors = {
    emergency: 'text-red-600 bg-red-50 border-red-200',
    urgent: 'text-orange-600 bg-orange-50 border-orange-200',
    standard: 'text-blue-600 bg-blue-50 border-blue-200',
  };
  return colors[urgency] || colors.standard;
};

/**
 * Sort array by date field
 */
export const sortByDate = (array, field, ascending = false) => {
  return [...array].sort((a, b) => {
    const dateA = new Date(a[field]);
    const dateB = new Date(b[field]);
    return ascending ? dateA - dateB : dateB - dateA;
  });
};

/**
 * Group array by field value
 */
export const groupBy = (array, field) => {
  return array.reduce((acc, item) => {
    const key = item[field];
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(item);
    return acc;
  }, {});
};

/**
 * Sleep/delay utility for simulations
 */
export const sleep = (ms) => {
  return new Promise(resolve => setTimeout(resolve, ms));
};
